// <s-announce> — slim announcement bar that crossfades between messages.
// All slides share one spot; the active one fades in. Shoppers can swipe left/right,
// use the arrows, or wait for the timer. The bar's colour follows the active slide's
// data-tone (red or black). Pauses on hover, focus and hidden tabs; no autoplay when
// the shopper prefers reduced motion. One component per file.
class SAnnounce extends HTMLElement {
  #ac = null;
  #timer = null;
  #index = 0;
  #paused = false;
  #startX = null;
  #startY = null;
  #swiped = false;

  connectedCallback() {
    this.track = this.querySelector("[data-announce-track]");
    if (!this.track) return;
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    this.reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

    this.querySelector("[data-announce-prev]")?.addEventListener("click", () => this.go(this.#index - 1), { signal });
    this.querySelector("[data-announce-next]")?.addEventListener("click", () => this.go(this.#index + 1), { signal });

    // Swipe: a mostly horizontal drag of 40px or more changes the slide and cancels the tap.
    this.track.addEventListener("pointerdown", (event) => {
      this.#startX = event.clientX;
      this.#startY = event.clientY;
      this.#swiped = false;
    }, { signal });
    this.track.addEventListener("pointerup", (event) => {
      if (this.#startX === null) return;
      const dx = event.clientX - this.#startX;
      const dy = event.clientY - this.#startY;
      this.#startX = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.#swiped = true;
      const rtl = getComputedStyle(this).direction === "rtl";
      const forward = rtl ? dx > 0 : dx < 0;
      this.go(this.#index + (forward ? 1 : -1));
    }, { signal });
    this.track.addEventListener("pointercancel", () => { this.#startX = null; }, { signal });
    this.track.addEventListener("click", (event) => {
      if (!this.#swiped) return;
      event.preventDefault();
      this.#swiped = false;
    }, { capture: true, signal });

    const pause = () => { this.#paused = true; this.#stop(); };
    const resume = () => { this.#paused = false; this.#start(); };
    this.addEventListener("pointerenter", (event) => { if (event.pointerType === "mouse") pause(); }, { signal });
    this.addEventListener("pointerleave", (event) => { if (event.pointerType === "mouse") resume(); }, { signal });
    this.addEventListener("focusin", pause, { signal });
    this.addEventListener("focusout", resume, { signal });
    document.addEventListener("visibilitychange", () => (document.hidden ? this.#stop() : this.#start()), { signal });

    this.#show(0);
    this.#start();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#stop();
  }

  get slides() {
    return [...this.track.children];
  }

  go(target) {
    const count = this.slides.length;
    if (!count) return;
    this.#show((target + count) % count);
    this.#start();
  }

  #show(index) {
    this.#index = index;
    const slide = this.slides[index];
    this.dataset.tone = slide?.dataset.tone || "black";
    this.slides.forEach((item, i) => {
      const active = i === index;
      item.toggleAttribute("data-active", active);
      item.toggleAttribute("inert", !active);
    });
  }

  #start() {
    this.#stop();
    if (this.#paused || this.reduce.matches || document.hidden || this.slides.length < 2) return;
    const delay = Number(this.dataset.delay) || 5000;
    this.#timer = setTimeout(() => this.go(this.#index + 1), delay);
  }

  #stop() {
    clearTimeout(this.#timer);
    this.#timer = null;
  }
}

if (!customElements.get("s-announce")) customElements.define("s-announce", SAnnounce);
